/**
 * Reloads a preview stand when a newer frontend is published to it.
 *
 * `pnpm web:push` replaces a stand's bundle in seconds (docs/STATIC.md), and
 * without this the person watching has to keep pressing reload to find out
 * whether it landed. The UI scratchpad has had this behaviour from the start;
 * this is the same idea applied to the real app.
 *
 * **The baseline is the page's own build, not the first answer from the
 * server.** `pnpm web:push` stamps `<meta name="static-build">` into the
 * index.html it publishes, and that is what the running page compares against.
 * Taking the first poll as the baseline instead would adopt a build published
 * in the second after the page loaded as "current", and the tab would then sit
 * on a stale bundle forever with nothing to say so.
 *
 * It exists only where that stamp does. Production's bundle is baked into an
 * image by apps/web/Dockerfile and never goes through `web:push`, so its
 * index.html carries no meta tag and none of this runs — no polling, no
 * listeners, nothing to reason about in front of customers.
 */
const MARKER_URL = '/static-build.json';
const META_NAME = 'static-build';

/** Slow enough to be invisible in the bucket's op count, fast enough to feel live. */
const INTERVAL_MS = 5_000;

interface StaticBuild {
  gitSha: string;
  builtAt: string;
}

/**
 * Whether a person is in the middle of typing something they would lose.
 *
 * A mockup has no state, so the scratchpad can reload whenever it likes. A real
 * screen has half-filled forms, and reloading one out from under somebody is a
 * worse failure than showing them a build that is a few seconds old. The
 * reload is deferred, never cancelled: the next tick takes it.
 */
function isBusyTyping(): boolean {
  const active = document.activeElement;
  if (!(active instanceof HTMLInputElement) && !(active instanceof HTMLTextAreaElement)) {
    return false;
  }
  return active.value.length > 0;
}

export function startLiveReload(): void {
  const stamp = document
    .querySelector<HTMLMetaElement>(`meta[name="${META_NAME}"]`)
    ?.content?.trim();

  // No stamp: production, or a dev server. Nothing to watch.
  if (!stamp) return;

  // `?live=0` for presenting a stand or taking screenshots, where the page
  // moving under you is the problem rather than the point. Same switch the
  // scratchpad uses, because two spellings of one idea is one too many.
  if (new URLSearchParams(window.location.search).get('live') === '0') return;

  const builtAt = Date.parse(stamp.split(' ')[1] ?? '');
  if (Number.isNaN(builtAt)) return;

  let stopped = false;

  const check = async () => {
    if (stopped || document.hidden) return;

    try {
      const response = await fetch(MARKER_URL, {
        cache: 'no-store',
        headers: { accept: 'application/json' },
      });
      if (!response.ok) return;

      const published = (await response.json()) as StaticBuild;
      const publishedAt = Date.parse(published.builtAt);

      // Strictly newer, never merely different. The marker is written *after*
      // index.html, so a page that loads in between reads a marker older than
      // itself — which on a `!==` comparison would reload straight into the
      // same page it is already showing.
      if (!(publishedAt > builtAt)) return;
      if (isBusyTyping()) return;

      stopped = true;
      window.location.reload();
    } catch {
      // A stand being redeployed refuses connections for a few seconds. That is
      // not a reason to say anything, and certainly not to stop watching.
    }
  };

  window.setInterval(check, INTERVAL_MS);
  // A tab that was in the background through a publish should catch up the
  // moment it is looked at, rather than up to five seconds later.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void check();
  });
}
